import Entity from "_framework/entities/entity";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
  UserEntityMemberInvitationParams,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import UserEntityInfo from "users/information/entity-information/user-entity-info";

export default class UserEntityMemberInvitation extends Entity<
  UserEntityMemberInvitation,
  UserEntityMemberInvitationJSON,
  UserEntityMemberInvitationParams
> {
  public static readonly EXAMPLE: UserEntityMemberInvitation = UserEntityMemberInvitation.create({
    id: Guid.generate(),
    userEntityInfo: UserEntityInfo.EXAMPLE,
    invitedEmail: EmailAddress.create("test@example.com"),
    registrationInfo: RegistrationInfo.EXAMPLE,
    status: InvitationStatus.Open,
  });

  public static create(values: UserEntityMemberInvitationParams): UserEntityMemberInvitation {
    const parsed = UserEntityMemberInvitationSchema.validate(values);
    return new UserEntityMemberInvitation(parsed);
  }

  public static fromJSON(json: unknown): UserEntityMemberInvitation | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = UserEntityMemberInvitationSchema.deserialize(json);
    return new UserEntityMemberInvitation(params);
  }

  public readonly id: Guid;
  public readonly userEntityInfo: UserEntityInfo;
  // TODO: Missing "userId: Guid" field.
  // TODO: Missing "expires: AHDate" field.
  // TODO: Missing "createClientRep: boolean" field.
  // TODO: Missing "createVendorRep: boolean" field.
  public readonly invitedEmail: EmailAddress; // TODO: Does not exist in the server-side UserEntityMemberInvitation class.
  public readonly registrationInfo: RegistrationInfo; // TODO: Does not exist in the server-side UserEntityMemberInvitation class.
  public readonly status: InvitationStatus;

  public constructor(params: UserEntityMemberInvitationParams) {
    super(params);
    this.id = params.id ?? Guid.generate();
    this.userEntityInfo = params.userEntityInfo;
    this.invitedEmail = params.invitedEmail;
    this.registrationInfo = RegistrationInfo.create({ params: params.registrationInfo });
    this.status = params.status ?? InvitationStatus.Open;
  }

  public async accept(): Promise<void> {
    if (this.status !== InvitationStatus.Open) {
      throw new InvalidUserEntityMemberInvitationStatusError("Cannot accept an invitation that is not open.");
    }
  }

  public toJSON(): UserEntityMemberInvitationJSON {
    return UserEntityMemberInvitationSchema.serialize(this);
  }

  public valueOf(): UserEntityMemberInvitationJSON {
    return this.toJSON();
  }

  public isEqualTo(other: UserEntityMemberInvitation | null | undefined): boolean {
    return this.id?.isEqualTo(other?.id ?? null) ?? false;
  }

  public toString(): string {
    return `UserEntityMemberInvitation {
      id: ${this.id?.value ?? "undefined"},
      entity: ${this.userEntityInfo.toString()},
      invitedEmail: ${this.invitedEmail.toString()},
      registrationInfo: ${this.registrationInfo?.toString() ?? "undefined"},
      status: ${this.status}
    }`;
  }
}

export class UserMemberInvitationNotFoundError extends Error {
  constructor(id: Guid) {
    super(`Member invitation with id ${id} not found.`);
  }
}
export class InvalidUserEntityMemberInvitationStatusError extends Error {}
export class InvalidUserEntityMemberInvitationError extends Error {}
